import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, AlertCircle, ArrowLeft, CheckCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { APP_ORIGIN } from '../../lib/domain';
import logoUrl from '../../src/styles/logo.png';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${APP_ORIGIN}/update-password`,
    });
    
    setLoading(false);
    if (error) {
      setError(error.message);
    } else {
      setSuccess(true);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-8">
          <img src={logoUrl} alt="RevertAI Logo" className="h-10 w-auto object-contain" />
          <span className="font-bold text-2xl tracking-tight text-white">
            Revert<span className="text-amber-500">AI</span>
          </span>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 shadow-2xl">
          {success ? (
            <div className="text-center py-4">
              <div className="flex justify-center mb-4">
                <CheckCircle size={40} className="text-amber-500" />
              </div>
              <h2 className="text-lg font-semibold text-white mb-2">Vérifiez votre e-mail</h2>
              <p className="text-sm text-slate-400 mb-6">
                Si un compte est associé à <strong className="text-white">{email}</strong>, un lien pour réinitialiser votre mot de passe a été envoyé.
              </p>
              <Link to="/login" className="flex items-center justify-center gap-2 text-amber-500 hover:text-amber-400 text-sm font-medium transition-colors">
                <ArrowLeft size={16} /> Retour à la connexion
              </Link>
            </div>
          ) : (
            <>
              <h1 className="text-xl font-semibold text-white mb-1">Mot de passe oublié</h1>
              <p className="text-sm text-slate-400 mb-6">Entrez votre e-mail pour recevoir un lien de réinitialisation.</p>

              {error && (
                <div className="flex items-start gap-2 bg-red-950/50 border border-red-800 rounded-lg px-4 py-3 mb-5 text-red-400 text-sm">
                  <AlertCircle size={16} className="shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Email</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail size={16} className="text-slate-500" />
                    </div>
                    <input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                      placeholder="vous@exemple.com"
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-10 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full bg-amber-600 hover:bg-amber-500 disabled:bg-amber-800 disabled:cursor-not-allowed text-white font-medium py-2.5 rounded-lg transition-colors text-sm mt-2"
                >
                  {loading ? 'Envoi...' : 'Envoyer le lien'}
                </button>
              </form>

              <div className="mt-6 text-center">
                <Link to="/login" className="flex items-center justify-center gap-2 text-slate-400 hover:text-slate-300 text-sm font-medium transition-colors">
                  <ArrowLeft size={16} /> Retour
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
